import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createResetSessionPrivateKey } from '../../features/auth/api/reset-session';
import { listPendingResets, resetPassword } from '../../features/auth/api/user-reset';
import { derivePrivateKey, getPublicKey } from '../../shared/lib/crypto';
import { PBKDF2_SALT, PBKDF2_ITERATIONS } from '../../shared/lib/auth-config';
import { parseBackendErrors } from '@/shared/lib/backend-errors';
import { exitToLogin } from '@/shared/lib/session-exit';
import type { PendingReset } from '../../features/auth/api/user-reset';
import { Button } from '@/shared/ui/button';
import { FormField } from '@/shared/ui/form-field';
import { LanguageToggle } from '@/shared/ui/language-toggle';
import { Logo } from '@/shared/ui/logo';
import { Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/shared/hooks/use-toast';
import { useLanguage } from '@/shared/hooks/use-language';

const RESET_PASSWORD_FIELD_MAP: Record<string, string> = {
  'public_key': 'password',
};

function ResetPassword() {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetData, setResetData] = useState<{ sessionId: string; sessionPrivateKey: any; resetId: string; email: string } | null>(null);

  useEffect(() => {
    const initializeReset = async () => {
      try {
        // Get session_id and session_secret from query params
        const sessionId = searchParams.get('session_id');
        const sessionSecret = searchParams.get('session_secret');

        if (!sessionId || !sessionSecret) {
          setError(t("resetPassword.invalidLink"));
          setIsInitializing(false);
          return;
        }

        // Create reset session private key from session_secret
        const sessionPrivateKey = createResetSessionPrivateKey(sessionSecret);

        // List pending resets to get the reset_id and email
        const resetsResult = await listPendingResets(sessionId, sessionPrivateKey);

        if (!resetsResult.success || !resetsResult.data?.resets || resetsResult.data.resets.length === 0) {
          setError(resetsResult.error || t("resetPassword.noResetFound"));
          setIsInitializing(false);
          return;
        }

        const reset = resetsResult.data.resets[0] as PendingReset;
        const resetId = reset.id;
        const email = reset.user_email;

        setResetData({
          sessionId,
          sessionPrivateKey,
          resetId,
          email
        });
      } catch (error) {
        console.error('❌ Failed to initialize reset:', error);
        setError('Failed to initialize password reset. Please try again.');
      } finally {
        setIsInitializing(false);
      }
    };

    initializeReset();
  }, [searchParams]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetData) {
      setError(t("resetPassword.passwordRequired"));
      return;
    }

    const localErrors: Record<string, string> = {};
    if (!password) {
      localErrors.password = t("resetPassword.passwordRequired") || 'Password is required';
    } else if (password.length < 8) {
      localErrors.password = t("resetPassword.passwordMinLength") || 'Password must be at least 8 characters';
    }
    if (!confirmPassword) {
      localErrors.confirmPassword = t("resetPassword.confirmRequired") || 'Please confirm your password';
    } else if (password && password !== confirmPassword) {
      localErrors.confirmPassword = t("resetPassword.passwordMismatch") || 'Passwords do not match';
    }
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setFieldErrors({});
    
    try {
      // Derive private key from email, password, and salt (same as login)
      const newPrivateKey = await derivePrivateKey(resetData.email, password, PBKDF2_SALT, PBKDF2_ITERATIONS);
      
      // Get public key from private key
      const publicKey = getPublicKey(newPrivateKey);
      
      // Convert public key to PEM format
      const publicKeyPem = publicKey.toPem();
      
      // Submit the reset
      const result = await resetPassword(
        resetData.sessionId,
        resetData.sessionPrivateKey,
        resetData.resetId,
        publicKeyPem
      );
      
      if (result.success) {
        setSuccess(true);
        toast({
          title: "Senha redefinida com sucesso",
          description: "Redirecionando para a página de login...",
        });
        // Redirect to login after 3 seconds
        setTimeout(() => {
          exitToLogin();
        }, 3000);
      } else {
        const errorMessage = result.error || 'Failed to reset password';
        const { fieldErrors: backendFieldErrors, generalError } = parseBackendErrors(
          result.validationError,
          RESET_PASSWORD_FIELD_MAP,
        );
        if (Object.keys(backendFieldErrors).length > 0) {
          setFieldErrors(backendFieldErrors);
          setError(generalError);
        } else {
          setError(errorMessage);
        }
        toast({
          title: "Erro",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('❌ Password reset error:', error);
      const errorMessage = 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
        <div className="w-full max-w-md p-8">
          <div className="mb-8 text-center">
            <Logo className="justify-center mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">
              {t("resetPassword.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("resetPassword.initializing")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
        <div className="w-full max-w-md p-8">
          <div className="mb-8 text-center">
            <Logo className="justify-center mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">
              {t("resetPassword.successTitle")}
            </h1>
          </div>

          <div className="bg-card p-8 rounded-2xl border border-border card-hover">
            <div className="bg-green-500/10 border border-green-500/20 text-green-500 px-4 py-3 rounded-md text-sm mb-6">
              <p className="font-semibold mb-2">{t("resetPassword.successMessage")}</p>
              <p className="text-sm">
                {t("resetPassword.successDetails")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
      <div className="w-full max-w-md p-8">
        <div className="mb-8 text-center">
          <Logo className="justify-center mb-4" />
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            {t("resetPassword.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("resetPassword.subtitle")}
          </p>
        </div>

        <div className="bg-card p-8 rounded-2xl border border-border card-hover">
          {resetData && (
            <p className="text-sm text-muted-foreground mb-6 text-center">
              {t("resetPassword.resettingFor")} <strong className="text-foreground">{resetData.email}</strong>
            </p>
          )}

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleResetPassword} className="space-y-6">
            <FormField
              id="password"
              type={showPassword ? "text" : "password"}
              label={t("resetPassword.newPassword")}
              placeholder="••••••••"
              value={password}
              onChange={(value) => {
                setPassword(value);
                if (fieldErrors.password) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.password;
                    return next;
                  });
                }
              }}
              error={fieldErrors.password}
              disabled={isLoading || !resetData}
              required
              autoComplete="new-password"
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="pr-3 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              }
            />

            <FormField
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              label={t("resetPassword.confirmPassword")}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(value) => {
                setConfirmPassword(value);
                if (fieldErrors.confirmPassword) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.confirmPassword;
                    return next;
                  });
                }
              }}
              error={fieldErrors.confirmPassword}
              disabled={isLoading || !resetData}
              required
              autoComplete="new-password"
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="pr-3 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              }
            />

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground btn-arrow"
              disabled={isLoading || !resetData}
            >
              {isLoading ? t("resetPassword.resetting") : t("resetPassword.submit")}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => exitToLogin()}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {t("resetPassword.backToLogin")}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <LanguageToggle />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
